<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { icons } from '@/plugins/icon'
import toTitleCase from '@/utils/toTitleCase'
import formatClaimsNatureLabel from '@/utils/formatClaimsNatureLabel'
import { useDashboardStore } from '@/store/dashboard'
import { usePortfolioStore } from '@/store/portfolio'
import type { NumericDictionary } from '@/types/common'
import '../../dashboardTable.css'

const dashboardStore = useDashboardStore()
const portfolioStore = usePortfolioStore()

// computed values from stores
const isBindedYears = computed<boolean>(() => props.snapshot?.isBindedYears ?? dashboardStore.isBindedYears)
const ratioAmount = computed(() => props.snapshot?.ratio_amount ?? dashboardStore.dashboards.ratio_amount)
const claimsType = computed<string[]>(() => props.claimsType || portfolioStore.parameters.claims_nature)
const normaliseSelection = computed<boolean[]>(() => {
  const expected = Math.max(0, claimsType.value.length - 1)
  const raw: boolean[] =
    props.snapshot?.normaliseSelection ?? portfolioStore.normaliseSelection ?? []
  const result = [...raw]
  while (result.length < expected) result.push(true)
  return result.slice(0, expected)
})
const seasonFactor = computed<boolean>(() => props.snapshot?.seasonFactor ?? dashboardStore.dashboards.seasonFactor)
const normalise = computed<boolean[]>(() => portfolioStore.normalise)
const ccrnlr = computed<string>(() => props.snapshot?.ccr_nlr ?? dashboardStore.dashboards.ccr_nlr)
const gwpnwp = computed(() => {
  return props.snapshot?.gwpnwp ?? dashboardStore.dashboards.gwpnwp
})
const uw_acc = computed(() => props.snapshot?.uw_acc ?? dashboardStore.dashboards.uw_acc)
const mqy = computed(() => props.snapshot?.mqy ?? dashboardStore.dashboards.mqy)
const underwriting_loss_ratios = computed(() => props.snapshot?.underwriting_loss_ratios ?? dashboardStore.underwriting_loss_ratios)
const props = defineProps<{
  totalMarginCcr: number;
  margin: NumericDictionary;
  totalMargin: number;
  leftColumnSize: number;
  visibleColumns?: number[];
  hideTotals?: boolean;
  claimsType?: string[];
  totalUltimateOnly?: boolean;
  lossRatiosOnly?: boolean;
  attritionalLargeExpanded?: boolean;
  snapshot?: any;
}>()

const visibleColumns = computed(() => props.visibleColumns || props.snapshot?.visibleColumns || dashboardStore.visibleColumns)

/** Pixel width of the Loss Ratio group for attritionalLargeExpanded (natures + collapsed Total). */
const expandedLossGroupWidth = computed(() => {
  const columnsPerType = underwriting_loss_ratios.value === 'Written' && uw_acc.value === 'uw' ? 6 : 5
  const naturesWidth = claimsType.value.reduce((sum, item) => {
    const marginVal = Number(props.margin?.[item] ?? 0)
    return sum + 112 + marginVal * (columnsPerType - 1)
  }, 0)
  return naturesWidth + 112 // collapsed Total
})

const expandedLossGroupColspan = computed(() => {
  const columnsPerType = underwriting_loss_ratios.value === 'Written' && uw_acc.value === 'uw' ? 6 : 5
  const naturesCols = claimsType.value.reduce((sum, item) => {
    const marginVal = Number(props.margin?.[item] ?? 0)
    return sum + (marginVal === 112 ? columnsPerType : 1)
  }, 0)
  return naturesCols + 1 // collapsed Total
})

function capitalizeFirstLetter(val: any) {
  return String(val).charAt(0).toUpperCase() + String(val).slice(1)
}

function removeBandFromName(name: string) {
  return name.replace(/Band/g, '')
}


function changeSeas() {
  dashboardStore.changeSeas()
}

function switchRatiosAmount() {
  dashboardStore.switch_ratio_amount()
}

function onChangeNormalised(index: number, val: boolean) {
  portfolioStore.setNormalisedSelection(index, val)
}


function changeCurrData() {
  dashboardStore.change_uw_acc()
}

function changeCurrentDispDate() {
  dashboardStore.change_mqy()
}

function changegwpnwp() {
  dashboardStore.switch_gwpnwp_amount()
}

function changeccrnlr() {
  dashboardStore.changeccrnlr()
}
const periodRightClick = ref(false)

function handleRightClick(event: any) {
  if (rightClickMenu.value) {
    rightClickMenu.value.showMenu(event)
  }
  periodRightClick.value = true
}

function handleClickAnywhere() {
  periodRightClick.value = false
}

onMounted(() => {
  document.addEventListener('click', handleClickAnywhere)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickAnywhere)
})

const rightClickMenu = ref<{ showMenu: (event: MouseEvent) => void } | null>(null)

function setDefaultPeriod() {
  if (portfolioStore.parameters['default_dashboard']) {
    const oldParameters = structuredClone(portfolioStore.parameters)
    portfolioStore.parameters['default_dashboard'].accident_underwriting = uw_acc.value
    portfolioStore.parameters['default_dashboard'].cohort = mqy.value

    portfolioStore.saveParameters(oldParameters, 'Saved Parameters')
  }
}

defineEmits(['onChangeCcrMargin'])
</script>

<template>
  <th
    class="fixWidth header-teal left-0 whitespace-nowrap fixHeight z-30 sticky"
    rowspan="2"
    @contextmenu.prevent.stop="handleRightClick($event)"
  >
    <p class="cursor-pointer item" data-testid="uw-acc-bespoke" @click="changeCurrData()">
      {{ { uw: isBindedYears ? 'Bespoke' : 'Underwriting', acc: 'Accident' }[uw_acc] }}
    </p>
    <p class="cursor-pointer item" data-testid="dashboard-mqy" @click="changeCurrentDispDate()">
      {{ toTitleCase(mqy) }}
    </p>
    <div v-if="periodRightClick" class="absolute -right-10 py-2 bg-gray-200 shadow-md rounded-sm">
      <span
        class="hover:bg-sybil-teal hover:bg-opacity-10 hover:text-sybil-teal p-1 px-3 cursor-pointer text-sm"
        @click="setDefaultPeriod"
        >Set as Default</span
      >
    </div>
  </th>
  <th v-if="visibleColumns?.includes(1)" class="fixWidth fixHeight header-teal text-[#55B691]" rowspan="2">
    <div class="cursor-pointer" data-testid="gwp-nwp-col" @click="changegwpnwp()">
            {{ gwpnwp }}
    </div>
    <span v-if="uw_acc == 'acc'" class="italc text-xs"
      >(underwriting {{ mqy }})</span
    >
  </th>
  <template v-if="portfolioStore.getExposureLength() > 0 && dashboardStore.isShowingExposure && !props.totalUltimateOnly">
    <th class="fixWidth fixHeight header-teal text-[#55B691]" rowspan="2">
      Total Risk Count
      <span v-if="uw_acc == 'acc'" class="italc text-xs"
        >(underwriting {{ mqy }})</span
      >
    </th>
    <th class="fixWidth fixHeight header-teal text-[#55B691]" rowspan="2">
      Average GWP
      <span v-if="uw_acc == 'acc'" class="italc text-xs"
        >(underwriting {{ mqy }})</span
      >
    </th>
    <th
      v-for="(i, idx) in portfolioStore.parameters['exposure'].slice(1)"
      :key="'exposure' + idx"
      class="fixWidth fixHeight header-teal text-[#55B691]"
      rowspan="2"
    >
      {{ capitalizeFirstLetter(i['method'] == 'sum' ? 'Total' : i['method']) }} {{ removeBandFromName(i['name']) }}
      <span v-if="uw_acc == 'acc'" class="italc text-xs"
        >(underwriting {{ mqy }})</span
      >
    </th>
  </template>
  <th v-if="visibleColumns?.includes(2)" class="fixWidth fixHeight header-teal text-[#55B691]" rowspan="2">
    <div class="cursor-pointer" data-testid="gep-nep-col" @click="changegwpnwp()">
      {{ gwpnwp?.replace('W', 'E') || gwpnwp }}
    </div>
  </th>
  <th
    class="lrfixWidth header-teal text-red-500 pt-1"
    :colspan="
      props.hideTotals 
        ? (
          // For attritional-only: calculate based on actual visible columns
          underwriting_loss_ratios === 'Written' && uw_acc === 'uw'
            ? 6  // Written mode: Paid, O/S, Incurred, IBNR, Unearned, Ultimate
            : 5  // Earned mode: Paid, O/S, Incurred, IBNR, Ultimate
        )
        : props.totalUltimateOnly
        ? (
          // For total-ultimate-only: calculate based on all visible columns
          claimsType.length + // Claims type columns (collapsed)
          (underwriting_loss_ratios === 'Written' && uw_acc === 'uw'
            ? 6  // Total Ultimate columns: Paid, O/S, Incurred, IBNR, Unearned, Ultimate
            : 5) // Total Ultimate columns: Paid, O/S, Incurred, IBNR, Ultimate
        )
        : props.lossRatiosOnly
        ? (
          // For loss-ratios-only: span the entire table width
          claimsType.length + 1 // Period + GWP + GEP + claims type columns
        )
        : props.attritionalLargeExpanded
        ? expandedLossGroupColspan
        : claimsType.length +
          1 +
          ((<any>Object).values(margin).reduce((ps: number, s: number) => ps + s, 0) / 112) * 4 +
          (totalMargin / 112) * 4
    "
    :style="{
      width: props.hideTotals 
        ? (
          // For attritional-only: calculate width based on actual visible columns
          underwriting_loss_ratios === 'Written' && uw_acc === 'uw'
            ? 6 * 112 + 'px'  // Written mode: 6 columns * 112px
            : 5 * 112 + 'px'  // Earned mode: 5 columns * 112px
        )
        : props.totalUltimateOnly
        ? (
          // For total-ultimate-only: calculate width based on all visible columns
          (claimsType.length * 112) + // Claims type columns (collapsed)
          (underwriting_loss_ratios === 'Written' && uw_acc === 'uw'
            ? 6 * 112  // Total Ultimate columns: Paid, O/S, Incurred, IBNR, Unearned, Ultimate
            : 5 * 112) + 'px' // Total Ultimate columns: Paid, O/S, Incurred, IBNR, Ultimate
        )
        : props.lossRatiosOnly
        ? (
          // For loss-ratios-only: span the entire table width
          '100%' // Span the entire table width
        )
        : props.attritionalLargeExpanded
        ? expandedLossGroupWidth + 'px'
        : (
          112 * (claimsType.length + 1) -
          dashboardStore.offMarginGWPGEP +
          (<any>Object).values(margin).reduce((ps: number, s: number) => ps + s, 0) *
            (underwriting_loss_ratios == 'Written' && uw_acc == 'uw'
              ? 5 - (visibleColumns?.includes(3) ? 0 : 3)
              : 4 - (visibleColumns?.includes(3) ? 0 : 2)) +
          totalMargin *
            (underwriting_loss_ratios == 'Written' && uw_acc == 'uw'
              ? 5 - (visibleColumns?.includes(3) ? 0 : 3)
              : 4 - (visibleColumns?.includes(3) ? 0 : 2)) +
          'px'
        ),
      'min-width': props.hideTotals 
        ? (
          underwriting_loss_ratios === 'Written' && uw_acc === 'uw'
            ? 6 * 112 + 'px'
            : 5 * 112 + 'px'
        )
        : props.totalUltimateOnly
        ? (
          // For total-ultimate-only: calculate min-width based on all visible columns
          (claimsType.length * 112) + // Claims type columns (collapsed)
          (underwriting_loss_ratios === 'Written' && uw_acc === 'uw'
            ? 6 * 112  // Total Ultimate columns: Paid, O/S, Incurred, IBNR, Unearned, Ultimate
            : 5 * 112) + 'px' // Total Ultimate columns: Paid, O/S, Incurred, IBNR, Ultimate
        )
        : props.lossRatiosOnly
        ? (
          // For loss-ratios-only: span the entire table width
          '100%' // Span the entire table width
        )
        : props.attritionalLargeExpanded
        ? expandedLossGroupWidth + 'px'
        : (
          112 * (claimsType.length + 1) +
          (<any>Object).values(margin).reduce((ps: number, s: number) => ps + s, 0) *
            (underwriting_loss_ratios == 'Written' && uw_acc == 'uw'
              ? 5 - (visibleColumns?.includes(3) ? 0 : 3)
              : 4 - (visibleColumns?.includes(3) ? 0 : 2)) +
          totalMargin *
            (underwriting_loss_ratios == 'Written' && uw_acc == 'uw'
              ? 5 - (visibleColumns?.includes(3) ? 0 : 3)
              : 4 - (visibleColumns?.includes(3) ? 0 : 2)) +
          'px'
        ),
      'max-width': props.hideTotals 
        ? (
          underwriting_loss_ratios === 'Written' && uw_acc === 'uw'
            ? 6 * 112 + 'px'
            : 5 * 112 + 'px'
        )
        : props.totalUltimateOnly
        ? (
          // For total-ultimate-only: calculate max-width based on all visible columns
          (claimsType.length * 112) + // Claims type columns (collapsed)
          (underwriting_loss_ratios === 'Written' && uw_acc === 'uw'
            ? 6 * 112  // Total Ultimate columns: Paid, O/S, Incurred, IBNR, Unearned, Ultimate
            : 5 * 112) + 'px' // Total Ultimate columns: Paid, O/S, Incurred, IBNR, Ultimate
        )
        : props.lossRatiosOnly
        ? (
          // For loss-ratios-only: span the entire table width
          '100%' // Span the entire table width
        )
        : props.attritionalLargeExpanded
        ? expandedLossGroupWidth + 'px'
        : (
          112 * (claimsType.length + 1) +
          (<any>Object).values(margin).reduce((ps: number, s: number) => ps + s, 0) *
            (underwriting_loss_ratios == 'Written' && uw_acc == 'uw'
              ? 5 - (visibleColumns?.includes(3) ? 0 : 3)
              : 4 - (visibleColumns?.includes(3) ? 0 : 2)) +
          totalMargin *
            (underwriting_loss_ratios == 'Written' && uw_acc == 'uw'
              ? 5 - (visibleColumns?.includes(3) ? 0 : 3)
              : 4 - (visibleColumns?.includes(3) ? 0 : 2)) +
          'px'
        ),
      height: '30px',
      transition: '0.5s ease-out',
    }"
  >
    <p v-if="uw_acc == 'acc'" style="display: inline">Earned</p>
    <p
      v-if="uw_acc == 'uw'"
      style="display: inline"
      class="cursor-pointer lossRatioItem"
      @click="dashboardStore.underwritingLossRatiosChange()"
    >
      {{ underwriting_loss_ratios + ' ' }}
    </p>
    Loss
    <p
      class="cursor-pointer lossRatioItem"
      data-testid="switch-ratio"
      style="display: inline"
      @click="switchRatiosAmount()"
    >
      {{ ' ' + toTitleCase(ratioAmount) }}
    </p>
  </th>

  <th v-if="visibleColumns?.includes(4)" class="fixWidth header-teal z-20 text-blue-400" rowspan="2">Comm.</th>
  <th v-if="visibleColumns?.includes(3) && !props.hideTotals" class="fixWidth header-teal z-20" rowspan="2">
    <div class="cursor-pointer" @click="changeccrnlr()">
      <p v-if="uw_acc == 'acc'">Earned</p>
      <p v-else>
        {{ underwriting_loss_ratios }}
      </p>
      {{ ccrnlr }}
    </div>
  </th>

  <th v-if="visibleColumns?.includes(3) && !props.hideTotals" class="fixWidth header-teal z-20 text-orange-900" rowspan="2">
    <div>
      <div class="normalise-dropdown">
        {{
          claimsType
            .slice(1)
            .filter((x: string, i: number) => normaliseSelection[i] == true)
            .map((x: any) =>
              normaliseSelection.filter((e: any) => e == true).length <= 1
                ? formatClaimsNatureLabel(x)
                : formatClaimsNatureLabel(x).slice(0, 1)
            )
            .join(', ')
        }}
        <div class="normalise-options" style="display: none;">
          <div v-for="(item, index) in claimsType.slice(1)" :key="index" class="normalise-option">
            <input
              type="checkbox"
              :id="`normalise-${index}`"
              :checked="normaliseSelection[index]"
              :disabled="normaliseSelection.filter((e) => e == true).length <= 1 && normaliseSelection[index]"
              @change="(e) => onChangeNormalised(index, (e.target as HTMLInputElement).checked)"
            />
            <label :for="`normalise-${index}`">{{ formatClaimsNatureLabel(item) }}</label>
          </div>
        </div>
      </div>
      Normalised<br />
      <p v-if="uw_acc == 'acc'">Earned</p>
      <p v-else>
        {{ underwriting_loss_ratios }}
      </p>

      <div class="cursor-pointer" @click="changeccrnlr()">
        {{ ccrnlr }}
      </div>
    </div>
  </th>
  <th v-if="visibleColumns?.includes(5) && !props.hideTotals" class="fixWidth header-teal z-20" rowspan="2" style="height: 90px">
    <p
      v-if="!seasonFactor"
      class="cursor-pointer item"
      style="display: inline"
      @click="changeSeas()"
    >
      No Seas.
    </p>
    <p
      v-if="seasonFactor"
      class="cursor-pointer item"
      style="display: inline"
      @click="changeSeas()"
    >
      Seas.
    </p>
    Adj.
    <p class="cursor-pointer">A-priori</p>
    <div class="cursor-pointer" @click="changeccrnlr()">
      {{ ccrnlr }}
    </div>
    <span v-if="totalMarginCcr == 0" class="cursor-pointer" @click="$emit('onChangeCcrMargin', 112 - totalMarginCcr)"
      ><component :is="icons.RiArrowRightLine"
    /></span>
    <span
      v-if="totalMarginCcr == 112"
      class="cursor-pointer"
      @click="$emit('onChangeCcrMargin', 112 - totalMarginCcr)"
      ><component :is="icons.RiArrowLeftLine"
    /></span>
  </th>
  <template v-if="visibleColumns?.includes(5) && !props.attritionalLargeExpanded">
    <template v-for="(i, idx_claims) in claimsType" :key="idx_claims">
      <th
        v-if="totalMarginCcr == 112"
        class="fixWidth header-teal z-10"
        rowspan="2"
        :class="seasonFactor ? '' : 'pb-5'"
      >
        {{ seasonFactor ? 'Seas. adj.' : ' ' }}<br />
        {{ formatClaimsNatureLabel(i) }}
        <br />
        {{ idx_claims == 0 ? ' A-priori' : ' Load' }}
      </th>

      <th v-if="totalMarginCcr == 112" class="fixWidth header-teal z-10" rowspan="2">
        <p class="item">{{ formatClaimsNatureLabel(i) }}<br />Seasonality</p>
      </th>
    </template>
  </template>
</template>
